/**
 * Shared studio modal + form framework.
 *
 * The shell owns dialog semantics, focus containment/return, Escape and
 * backdrop dismissal, and the common header/body/footer layout. Entity
 * panels should use `openFormModal`; full-surface tools can use `openModal`.
 */

import "./shared.css";

export type ModalCloseReason =
  | "backdrop"
  | "cancel"
  | "close-button"
  | "escape"
  | "programmatic"
  | "submit";

export interface ModalHandle {
  /** The scrollable content area to render into. */
  body: HTMLElement;
  /** Programmatically close + tear down the modal. */
  close: (reason?: ModalCloseReason) => void;
  /** The outer root element. */
  root: HTMLElement;
  /** The actual element carrying role=dialog. */
  dialog: HTMLElement;
  /** Stable shell regions for advanced category-specific content. */
  header: HTMLElement;
  footer: HTMLElement;
  /** Move focus to the configured first control (or the first focusable). */
  focusInitial: () => void;
}

export interface ModalOptions {
  title: string;
  /** Optional context rendered beneath the title and wired to aria-describedby. */
  description?: string;
  /** Extra class on the dialog for size/skin variants. */
  variant?: string;
  /** Fired once the modal is fully removed. */
  onClose?: (reason: ModalCloseReason) => void;
  /** Mount target (defaults to document.body). */
  host?: HTMLElement;
  /** Stable Koko/test surface key on the root. */
  surface?: string;
  /** A selector or element to focus after mounting; false focuses the dialog. */
  initialFocus?: string | HTMLElement | false;
  /** Override the element that receives focus after close. */
  returnFocus?: HTMLElement | null;
  /** Defaults true. Disable only for an operation that cannot safely dismiss. */
  closeOnEscape?: boolean;
  /** Defaults true. Disable only for an operation that cannot safely dismiss. */
  closeOnBackdrop?: boolean;
  /** Defaults true. */
  showCloseButton?: boolean;
  /** Override the Koko action on the header close control. */
  closeAction?: string;
}

export interface FormModalHandle extends ModalHandle {
  form: HTMLFormElement;
  cancelButton: HTMLButtonElement;
  submitButton: HTMLButtonElement;
  /** Disable dismissal/actions and expose a stable busy state. */
  setBusy: (busy: boolean, label?: string) => void;
}

export interface FormModalOptions extends ModalOptions {
  submitLabel?: string;
  cancelLabel?: string;
  submitAction?: string;
  cancelAction?: string;
  /** Return false to keep the modal open (for validation). */
  onSubmit: (
    event: SubmitEvent,
    modal: FormModalHandle,
  ) => boolean | void | Promise<boolean | void>;
  onCancel?: () => void;
}

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[contenteditable='true']",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

let modalId = 0;
let bodyOverflowBeforeModals = "";
const backgroundInert = new Map<HTMLElement, boolean>();

function syncDocumentModalState(): void {
  const roots = Array.from(
    document.body.querySelectorAll<HTMLElement>(":scope > .studio-modal-root"),
  );
  if (roots.length > 0) {
    if (backgroundInert.size === 0) {
      bodyOverflowBeforeModals = document.body.style.overflow;
      for (const child of Array.from(document.body.children)) {
        if (
          !(child instanceof HTMLElement) ||
          child.classList.contains("studio-modal-root")
        ) {
          continue;
        }
        backgroundInert.set(child, child.inert);
        child.inert = true;
      }
    }
    document.body.style.overflow = "hidden";
    roots.forEach((root, index) => {
      const top = index === roots.length - 1;
      root.inert = !top;
      if (top) root.removeAttribute("aria-hidden");
      else root.setAttribute("aria-hidden", "true");
    });
    return;
  }
  document.body.style.overflow = bodyOverflowBeforeModals;
  for (const [element, wasInert] of backgroundInert) {
    if (element.isConnected) element.inert = wasInert;
  }
  backgroundInert.clear();
}

function focusableElements(dialog: HTMLElement): HTMLElement[] {
  return Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (element) =>
      !element.hidden &&
      element.getAttribute("aria-hidden") !== "true" &&
      element.getClientRects().length > 0,
  );
}

function isTopModal(root: HTMLElement): boolean {
  const open = Array.from(
    document.querySelectorAll<HTMLElement>(".studio-modal-root"),
  );
  return open[open.length - 1] === root;
}

interface Shell extends ModalHandle {
  content: HTMLElement;
  setDismissLocked: (locked: boolean) => void;
}

function createShell(options: ModalOptions): Shell {
  const host = options.host ?? document.body;
  const previouslyFocused =
    options.returnFocus === undefined
      ? document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null
      : options.returnFocus;
  const id = ++modalId;

  const root = document.createElement("div");
  root.className = "studio-modal-root";
  root.dataset.open = "false";
  if (options.surface) root.dataset.kokoSurface = options.surface;

  const scrim = document.createElement("div");
  scrim.className = "studio-modal-scrim";
  scrim.setAttribute("aria-hidden", "true");

  const dialog = document.createElement("div");
  dialog.className = "studio-modal-dialog";
  if (options.variant) dialog.classList.add(options.variant);
  dialog.setAttribute("role", "dialog");
  dialog.setAttribute("aria-modal", "true");
  dialog.setAttribute("aria-labelledby", `studio-modal-title-${id}`);
  dialog.tabIndex = -1;

  const header = document.createElement("header");
  header.className = "studio-modal-header";
  const heading = document.createElement("div");
  heading.className = "studio-modal-heading";
  const titleEl = document.createElement("h2");
  titleEl.id = `studio-modal-title-${id}`;
  titleEl.className = "studio-modal-title";
  titleEl.textContent = options.title;
  heading.appendChild(titleEl);
  if (options.description) {
    const description = document.createElement("p");
    description.id = `studio-modal-description-${id}`;
    description.className = "studio-modal-description";
    description.textContent = options.description;
    dialog.setAttribute("aria-describedby", description.id);
    heading.appendChild(description);
  }
  header.appendChild(heading);

  const closeButton = document.createElement("button");
  closeButton.type = "button";
  closeButton.className = "studio-modal-close";
  closeButton.setAttribute("aria-label", `Close ${options.title}`);
  closeButton.dataset.kokoAction = options.closeAction ?? "modal-close";
  closeButton.textContent = "×";
  if (options.showCloseButton !== false) header.appendChild(closeButton);

  const content = document.createElement("div");
  content.className = "studio-modal-content";
  const body = document.createElement("div");
  body.className = "studio-modal-body";
  const footer = document.createElement("footer");
  footer.className = "studio-modal-footer";
  footer.hidden = true;
  content.append(body, footer);
  dialog.append(header, content);
  root.append(scrim, dialog);
  host.appendChild(root);
  if (host === document.body) syncDocumentModalState();

  let closed = false;
  let dismissLocked = false;

  const focusInitial = (): void => {
    let target: HTMLElement | null = null;
    if (options.initialFocus instanceof HTMLElement) {
      target = options.initialFocus;
    } else if (typeof options.initialFocus === "string") {
      target = dialog.querySelector<HTMLElement>(options.initialFocus);
    } else if (options.initialFocus !== false) {
      target =
        focusableElements(dialog).find((el) => el !== closeButton) ?? closeButton;
    }
    if (target && !target.matches(FOCUSABLE)) {
      target = target.querySelector<HTMLElement>(FOCUSABLE);
    }
    (target?.isConnected ? target : dialog).focus();
  };

  const close = (reason: ModalCloseReason = "programmatic"): void => {
    if (closed || (dismissLocked && reason !== "submit")) return;
    closed = true;
    document.removeEventListener("keydown", onKeyDown, true);
    document.removeEventListener("focusin", onFocusIn, true);
    root.dataset.open = "false";
    window.setTimeout(() => {
      root.remove();
      if (host === document.body) syncDocumentModalState();
      if (previouslyFocused?.isConnected) previouslyFocused.focus();
      options.onClose?.(reason);
    }, 200);
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    if (!isTopModal(root)) return;
    if (event.key === "Escape" && options.closeOnEscape !== false && !dismissLocked) {
      event.preventDefault();
      event.stopPropagation();
      close("escape");
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = focusableElements(dialog);
    if (focusable.length === 0) {
      event.preventDefault();
      dialog.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !dialog.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const onFocusIn = (event: FocusEvent): void => {
    if (!closed && isTopModal(root) && !dialog.contains(event.target as Node)) {
      focusInitial();
    }
  };

  scrim.addEventListener("click", () => {
    if (options.closeOnBackdrop !== false && !dismissLocked) close("backdrop");
  });
  closeButton.addEventListener("click", () => close("close-button"));
  document.addEventListener("keydown", onKeyDown, true);
  document.addEventListener("focusin", onFocusIn, true);

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (closed) return;
      root.dataset.open = "true";
      focusInitial();
    });
  });

  return {
    body,
    close,
    root,
    dialog,
    header,
    footer,
    content,
    focusInitial,
    setDismissLocked: (locked) => {
      dismissLocked = locked;
      closeButton.disabled = locked;
      root.dataset.busy = String(locked);
    },
  };
}

/** Open a centered modal for custom/full-surface content. */
export function openModal(options: ModalOptions): ModalHandle {
  return createShell(options);
}

/** Open the standard entity form shell with Cancel + Save footer actions. */
export function openFormModal(options: FormModalOptions): FormModalHandle {
  const shell = createShell(options);
  const form = document.createElement("form");
  form.className = "studio-modal-form";
  shell.content.replaceWith(form);
  form.append(shell.body, shell.footer);
  shell.footer.hidden = false;

  const cancelButton = document.createElement("button");
  cancelButton.type = "button";
  cancelButton.className = "studio-button studio-modal-cancel";
  cancelButton.textContent = options.cancelLabel ?? "Cancel";
  cancelButton.dataset.kokoAction =
    options.cancelAction ?? (options.surface ? `${options.surface}-cancel` : "modal-cancel");

  const submitButton = document.createElement("button");
  submitButton.type = "submit";
  submitButton.className = "studio-button is-primary studio-modal-submit";
  submitButton.textContent = options.submitLabel ?? "Save changes";
  submitButton.dataset.kokoAction =
    options.submitAction ?? (options.surface ? `${options.surface}-save` : "modal-save");
  const idleLabel = submitButton.textContent;
  shell.footer.append(cancelButton, submitButton);

  let busy = false;
  const setBusy = (nextBusy: boolean, label = "Saving…"): void => {
    busy = nextBusy;
    shell.setDismissLocked(nextBusy);
    cancelButton.disabled = nextBusy;
    submitButton.disabled = nextBusy;
    submitButton.textContent = nextBusy ? label : idleLabel;
    form.setAttribute("aria-busy", String(nextBusy));
  };

  const handle: FormModalHandle = {
    ...shell,
    form,
    cancelButton,
    submitButton,
    setBusy,
  };

  cancelButton.addEventListener("click", () => {
    if (busy) return;
    options.onCancel?.();
    shell.close("cancel");
  });
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    if (busy) return;
    void Promise.resolve(options.onSubmit(event as SubmitEvent, handle)).then(
      (shouldClose) => {
        if (shouldClose !== false) shell.close("submit");
      },
    );
  });

  return handle;
}
